import { TpDataContext } from '../interfaces';
import {
  DerivedColumnGroupMetadata,
  DerivedColumnMetadata,
  MUTATION_LEDGER_VERSION,
  MutationLedgerPayloadV1,
} from './schemas';

/**
 * Adds the final metadata summary for one derived column and its group.
 *
 * `columnInfo` and `columnGroupInfo` are denormalized, advisory summaries included in the v1
 * clipboard contract for inspection and future schema checks. They are not replay instructions:
 * the ordered action payloads, including their per-column metadata, remain authoritative.
 */
function addColumnSummary(
  columnInfo: Record<string, Record<string, DerivedColumnMetadata>>,
  columnGroupInfo: Record<string, Record<string, DerivedColumnGroupMetadata>>,
  tableId: string,
  columnId: string,
  metadata: DerivedColumnMetadata,
) {
  (columnInfo[tableId] ??= {})[columnId] = { ...metadata };
  if (metadata.groupId && metadata.groupName) {
    (columnGroupInfo[tableId] ??= {})[metadata.groupId] = {
      name: metadata.groupName,
      description: metadata.groupDescription,
    };
  }
}

/**
 * Converts the live ordered ledger into its JSON-safe, version-one clipboard representation.
 *
 * The returned summaries describe the final derived-column and derived-group metadata by table.
 * They intentionally omit computed column data and are advisory only; replay uses the ordered
 * `actions` array because it preserves dependencies, atomic action boundaries, and authoritative
 * recipe metadata.
 */
export function mapMutationLedgerToDTO(dataContext: TpDataContext, assetType: string): MutationLedgerPayloadV1 {
  const ledger = dataContext.mutationLedger;
  const columnInfo: Record<string, Record<string, DerivedColumnMetadata>> = {};
  const columnGroupInfo: Record<string, Record<string, DerivedColumnGroupMetadata>> = {};

  for (const action of ledger.actions) {
    if (action.type === 'DELETE_COLUMN') continue;
    if (action.payload.type === 'TABLE_JOIN') {
      for (const column of action.payload.columns) {
        addColumnSummary(columnInfo, columnGroupInfo, action.targetTableId, column.destColId, column.columnMetadata);
      }
    } else {
      addColumnSummary(
        columnInfo,
        columnGroupInfo,
        action.targetTableId,
        action.targetColumnIds[0],
        action.payload.columnMetadata,
      );
    }
  }

  return {
    version: MUTATION_LEDGER_VERSION,
    sourceAssetId: dataContext.assetId,
    sourceAssetName: dataContext.name,
    assetType,
    columnInfo,
    columnGroupInfo,
    actions: structuredClone(ledger.actions),
  };
}
